import { VerifiedEmailHelp } from '../../components/team/VerifiedEmailHelp';
import { guestRsvp } from '../../services/guest-rsvp.service';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { Guest, GuestStatus } from '../../types/guest.types';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/shared/Button';
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// Dietary options moved inside component for translation

export function RSVPPage() {
  const { weddingId } = useParams<{ weddingId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [verificationRevision, setVerificationRevision] = useState(0);
  const [eligibleGuests, setEligibleGuests] = useState<Guest[]>([]);
  const { t, i18n } = useTranslation(['rsvp', 'common']);

  const DIETARY_KEYS = [
    { key: 'vegetarian', label: t('form.dietary.vegetarian') },
    { key: 'vegan', label: t('form.dietary.vegan') },
    { key: 'glutenFree', label: t('form.dietary.glutenFree') },
    { key: 'porkFree', label: t('form.dietary.porkFree') },
    { key: 'nutAllergy', label: t('form.dietary.nutAllergy') },
    { key: 'seafoodAllergy', label: t('form.dietary.seafoodAllergy') },
  ];

  const { currentUser, signOut, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weddingDetails, setWeddingDetails] = useState<any>(null);

  // Search state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Guest State
  const [guest, setGuest] = useState<Guest | null>(null);

  // Form State
  const [attending, setAttending] = useState<boolean | null>(null);
  const [dietaryRestrictions, setDietaryRestrictions] = useState<string[]>([]);
  const [customDiet, setCustomDiet] = useState('');
  const [plusOneName, setPlusOneName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const selectGuest = (found: Guest) => {
    setGuest(found);
    setAttending(
      found.status === GuestStatus.CONFIRMED
        ? true
        : found.status === GuestStatus.DECLINED
          ? false
          : null
    );
    setDietaryRestrictions(found.dietaryRestrictions || []);
    setNotes(found.notes || '');
    setPlusOneName(found.plusOneName || '');
  };
  useEffect(() => {
    let cancelled = false;
    setGuest(null);
    setEligibleGuests([]);
    setSuccess(false);
    setError(null);
    if (authLoading) return;
    if (!currentUser?.emailVerified || currentUser.isAnonymous || !weddingId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    guestRsvp('load', { eventId: weddingId })
      .then(result => {
        if (cancelled) return;
        setWeddingDetails(result.details);
        setEligibleGuests(result.guests);
        if (result.guests.length === 1) selectGuest(result.guests[0]);
      })
      .catch(() => {
        if (!cancelled) setError(t('team.rsvpNoMatch', { ns: 'dashboard' }));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [weddingId, currentUser?.uid, currentUser?.emailVerified, authLoading, verificationRevision]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !weddingId) return;

    setSearchLoading(true);
    setSearchError(null);

    try {
      const found = eligibleGuests.find(
        g =>
          g.firstName.toLowerCase() === firstName.trim().toLowerCase() &&
          (!lastName.trim() || g.lastName.toLowerCase() === lastName.trim().toLowerCase())
      );
      if (found) {
        setGuest(found);
        setAttending(
          found.status === GuestStatus.CONFIRMED
            ? true
            : found.status === GuestStatus.DECLINED
              ? false
              : null
        );
        setDietaryRestrictions(found.dietaryRestrictions || []);
        setNotes(found.notes || '');
        setPlusOneName(found.plusOneName || '');
      } else {
        setSearchError(t('errors.searchError'));
      }
    } catch (err) {
      console.error('Search error', err);
      setSearchError(t('errors.genericError'));
    } finally {
      setSearchLoading(false);
    }
  };

  const toggleDietary = (option: string) => {
    setDietaryRestrictions(prev =>
      prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guest || attending === null) return;

    setSubmitLoading(true);
    try {
      const allDietary = [...dietaryRestrictions];
      if (customDiet.trim()) allDietary.push(`Autre: ${customDiet.trim()}`);

      await guestRsvp('respond', {
        eventId: weddingId,
        guestId: guest.id,
        response: {
          status: attending ? GuestStatus.CONFIRMED : GuestStatus.DECLINED,
          dietaryRestrictions: allDietary,
          notes: notes,
          ...(guest.plusOne ? { plusOneName } : {}),
        },
      });

      setSuccess(true);
    } catch (err) {
      console.error('Submit error:', err);
      alert(t('errors.submitError'));
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!authLoading && currentUser && !currentUser.isAnonymous && !currentUser.emailVerified)
    return (
      <main className="max-w-lg mx-auto p-6">
        <VerifiedEmailHelp onVerified={() => setVerificationRevision(r => r + 1)} />
      </main>
    );
  if (!authLoading && (!currentUser || currentUser.isAnonymous || !currentUser.emailVerified))
    return (
      <main className="max-w-lg mx-auto p-6 space-y-5">
        <h1 className="text-2xl font-serif">RSVP</h1>
        <p>{t('team.rsvpIdentity', { ns: 'dashboard' })}</p>
        <Link to="/login" state={{ from: location }} className="block underline">
          {t('team.signIn', { ns: 'dashboard' })}
        </Link>
      </main>
    );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Oups !</h2>
          <p className="text-gray-600">{error}</p>
          <Button
            className="mt-4"
            onClick={async () => {
              await signOut();
              navigate('/login', { state: { from: location } });
            }}
          >
            {t('team.switchAccount', { ns: 'dashboard' })}
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center transform transition-all animate-fade-in-up">
          <CheckCircleIcon className="h-20 w-20 text-green-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('success.title')}</h2>
          <p className="text-lg text-gray-600 mb-6">{t('success.message')}</p>
          {attending && weddingDetails?.date && (
            <div className="bg-primary-50 rounded-xl p-4 text-primary-800">
              <p className="font-medium">
                {t('success.dateMessage', {
                  date: new Date(weddingDetails.date).toLocaleDateString(i18n.language, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }),
                })}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const rsvpConfig = (weddingDetails as any)?.rsvpConfig || {};

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4 z-10 flex items-center bg-white rounded-full shadow-md px-3 py-1.5 border border-gray-100">
        <GlobeAltIcon className="h-4 w-4 text-gray-500 mr-2" />
        <select
          value={i18n.language.split('-')[0]}
          onChange={e => {
            i18n.changeLanguage(e.target.value);
            localStorage.setItem('language', e.target.value);
          }}
          className="bg-transparent text-sm text-gray-700 focus:outline-none border-none appearance-none cursor-pointer pr-4"
          style={{ background: 'transparent' }}
        >
          <option value="fr">Français</option>
          <option value="en">English</option>
          <option value="es">Español</option>
          <option value="de">Deutsch</option>
          <option value="it">Italiano</option>
        </select>
      </div>

      <div className="max-w-xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-serif text-primary-600 mb-4">RSVP</h1>
          <p className="text-gray-600">
            {rsvpConfig.customWelcomeMessage || t('header.defaultWelcome')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden transition-all">
          {!guest ? (
            <div className="p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <MagnifyingGlassIcon className="h-6 w-6 text-primary-500 mr-2" />
                {t('search.title')}
              </h2>

              {searchError && (
                <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg text-sm">
                  {searchError}
                </div>
              )}

              <form onSubmit={handleSearch} className="space-y-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    {t('search.firstName')}
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 p-3 bg-white text-gray-900 border"
                    placeholder={t('search.firstNamePlaceholder')}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    {t('search.lastName')}
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 p-3 bg-white text-gray-900 border"
                    placeholder={t('search.lastNamePlaceholder')}
                  />
                </div>
                <Button type="submit" className="w-full py-3" loading={searchLoading}>
                  {searchLoading ? t('search.searching') : t('search.submit')}
                </Button>
              </form>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 space-y-8 animate-fade-in-up">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('form.greeting', { firstName: guest.firstName, lastName: guest.lastName })}
                </h2>
                <p className="text-gray-500 mt-1">{t('form.subtitle')}</p>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">{t('form.attendance')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setAttending(true)}
                    className={`p-4 rounded-xl border-2 transition-all ${attending === true ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-700 hover:border-primary-200 hover:bg-gray-50'}`}
                  >
                    {t('form.yes')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAttending(false)}
                    className={`p-4 rounded-xl border-2 transition-all ${attending === false ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 bg-white text-gray-700 hover:border-red-200 hover:bg-gray-50'}`}
                  >
                    {t('form.no')}
                  </button>
                </div>
              </div>

              {attending && (
                <>
                  {guest.plusOne && (
                    <div className="space-y-4 pt-6 border-t border-gray-100">
                      <h3 className="text-lg font-medium text-gray-900">
                        {t('form.plusOne.title')}
                      </h3>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          {t('form.plusOne.label')}
                        </label>
                        <input
                          type="text"
                          aria-label={t('form.plusOne.label')}
                          value={plusOneName}
                          maxLength={200}
                          onChange={e => setPlusOneName(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 p-3 bg-white text-gray-900 border"
                          placeholder={t('form.plusOne.placeholder')}
                        />
                      </div>
                    </div>
                  )}

                  {rsvpConfig.askDietary && (
                    <div className="space-y-4 pt-6 border-t border-gray-100">
                      <h3 className="text-lg font-medium text-gray-900">
                        {t('form.dietary.title')}
                      </h3>
                      <p className="text-sm text-gray-500">{t('form.dietary.subtitle')}</p>
                      <div className="grid grid-cols-2 gap-3">
                        {DIETARY_KEYS.map(opt => (
                          <label
                            key={opt.key}
                            className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={dietaryRestrictions.includes(opt.label)}
                              onChange={() => toggleDietary(opt.label)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 rounded border-gray-300"
                            />
                            <div className="text-sm text-gray-700">{opt.label}</div>
                          </label>
                        ))}
                      </div>
                      <input
                        type="text"
                        aria-label={t('form.dietary.title')}
                        value={customDiet}
                        maxLength={190}
                        onChange={e => setCustomDiet(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 p-3 bg-white text-gray-900 border mt-3"
                        placeholder={t('form.dietary.customPlaceholder')}
                      />
                    </div>
                  )}
                </>
              )}

              {rsvpConfig.askMessage && (
                <div className="space-y-4 pt-6 border-t border-gray-100">
                  <h3 className="text-lg font-medium text-gray-900">{t('form.message.title')}</h3>
                  <textarea
                    aria-label={t('form.message.title')}
                    value={notes}
                    maxLength={2000}
                    onChange={e => setNotes(e.target.value)}
                    rows={3}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 p-3 bg-white text-gray-900 border dark:bg-gray-800 dark:text-white"
                    placeholder={t('form.message.placeholder')}
                  />
                </div>
              )}

              <div className="pt-6">
                <Button
                  type="submit"
                  className="w-full py-4 text-lg"
                  loading={submitLoading}
                  disabled={attending === null}
                >
                  {submitLoading ? t('form.submitting') : t('form.submit')}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
